<template>
  <div class="customer-list">
    <!-- 客户管理-客户列表（企业微信手机端） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="客户名称/手机号" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，点开选择框选值或清除，超过一屏横向滑动 -->
      <div class="filter-row">
        <mobile-condition-select v-model="searchInfo.category" label-column="dictName" code-column="dictCode"
                                 :data="customerCategory" type="tree" placeholder="客户分类"
                                 @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.customerType" label-column="dictName" code-column="userCode"
                                 :data="customerTypeList" type="list" placeholder="客户类型"
                                 @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.industryId" label-column="dictName" code-column="userCode"
                                 :data="industryList" type="list" placeholder="所属行业"
                                 @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.cloudType" label-column="dictName" code-column="userCode"
                                 :data="cloudTypeList" type="list" placeholder="是否云客户"
                                 @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.softType" label-column="dictName" code-column="userCode"
                                 :data="softTypeList" type="list" placeholder="软件类型"
                                 @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.ownerEmployeeId" label-column="employeeName" code-column="id"
                                 :data="employeeList" type="list" placeholder="负责人"
                                 @change="resetSearch"/>
      </div>
    </mobile-list-header>
    <mobile-list :loading="loading" :has-more="searchInfo.hasMore" :query-list="queryList" :refresh="resetSearch" ref="mobileList">
      <list-card class="customer-item" v-for="item in dataList" :key="item.id" :label="item.merchantRemarkName" @click="toDetail(item)">
        <template #desc>
          <span class="type-tag" :class="item.contactType === 1 ? 'person' : 'company'">{{ contactTypeName(item.contactType) }}</span>
          <span v-if="dictName(customerTypeList, item.customerType)" class="type-tag company">{{ dictName(customerTypeList, item.customerType) }}</span>
          <span v-if="Number(item.cloudType) === CLOUD_TYPE.YES" class="type-tag person">云客户</span>
          <yq-icon class="item-arrow" name="element-ui_arrow-right"/>
        </template>
        <data-column label="联系电话">{{ item.merchantRemarkPhone || "-" }}</data-column>
        <data-column label="所属行业">{{ dictName(industryList, item.industryId) || "-" }}</data-column>
        <data-column label="联系人">{{ item.merchantRemarkContact || "-" }}</data-column>
        <data-column label="地区">
          <administrative v-model="item.merchantRegion" :disabled="true"/>
        </data-column>
        <data-column label="负责人">{{ item.ownerEmployeeName || "-" }}</data-column>
      </list-card>
    </mobile-list>
    <!-- 悬浮「新增客户」按钮：右下角，可拖动 -->
    <mobile-float-button label="新增客户" icon="element-ui_plus" @click="toCreate"/>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import Administrative from "@/components/h5/data/Administrative.vue";
import MobileFloatButton from "@/components/qywx/button/MobileFloatButton.vue";
import YqIcon from "@/components/YqIcon.vue";
import customerApi from "@/api/normal/common/customerApi";
import commonApi from "@/api/commonApi";
import {
  CLOUD_TYPE, DICT_CUSTOMER_SOFT_TYPE, DICT_CUSTOMER_TYPE, DICT_INDUSTRY_TYPE, contactTypeName, dictName
} from "@/views/h5/crm/crmDictionary";

/** 是否云客户是类型字段（非字典），自己拼一份给查询条件用 */
const CLOUD_TYPE_LIST = [
  {dictName: "是", userCode: CLOUD_TYPE.YES},
  {dictName: "否", userCode: CLOUD_TYPE.NO},
];

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "CL",
  components: {
    YqIcon, Administrative, DataColumn, ListCard, MobileConditionSelect, MobileFloatButton, MobileListHeader, MobileList
  },
  data() {
    return {
      loading: false,
      searchInfo: {
        searchKey: "",
        category: "",
        // crm 查询条件（字典值存 userCode）
        customerType: "",
        industryId: "",
        softType: "",
        ownerEmployeeId: "",
        // cloudType 是后端 Integer，空值用 "" 提交前会被过滤掉
        cloudType: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
      customerCategory: [], // 客户分类
      // 字典：后端只返回值，名称由前端关联（规则12）
      customerTypeList: [],
      industryList: [],
      softTypeList: [],
      cloudTypeList: CLOUD_TYPE_LIST,
      employeeList: [], // 负责人查询条件用
      // 常量暴露给模板用
      CLOUD_TYPE,
    };
  },
  created() {
    commonApi.queryDictionaryTree("CUSTOMER_CATEGORY").then(list => this.customerCategory = list);
    commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_TYPE).then(list => this.customerTypeList = list || []);
    commonApi.queryUniqueDictionaryList(DICT_INDUSTRY_TYPE).then(list => this.industryList = list || []);
    commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_SOFT_TYPE).then(list => this.softTypeList = list || []);
    commonApi.queryEmployeeList({}).then(list => this.employeeList = list || []);
  },
  methods: {
    dictName,
    contactTypeName,
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      // 空值不提交，避免空串绑到后端 Integer（cloudType）报错
      const params = {};
      Object.keys(this.searchInfo).forEach(k => {
        const v = this.searchInfo[k];
        if (v === "" || v === null || v === undefined || k === "hasMore") {
          return;
        }
        params[k] = v;
      });
      customerApi.queryList(params).then(res => {
        this.dataList = this.dataList.concat(res.records);
        this.searchInfo.totalCount = res.totalCount;
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    // 重置查询条件，从第一页开始查
    resetSearch() {
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
      this.queryList();
    },
    // 清空关键字
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    // 查看/编辑客户
    toDetail(data) {
      this.router.push(`/qywx/partner/customer/detail?id=${data.id}`);
    },
    // 新增客户（详情页不带 id 即新建）
    toCreate() {
      this.router.push("/qywx/partner/customer/detail");
    }
  }
};
</script>

<style lang="scss" scoped>
.customer-list{
  height: 100%;
  display: flex;
  flex-direction: column;
  // 悬浮按钮的定位祖先
  position: relative;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    // 和右边的「搜索」按钮留出间距，避免清空按钮贴着按钮
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动（可放 5 个以上）
.filter-row{
  display: flex;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
.customer-item{
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .type-tag{
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    &.person{
      color: var(--el-color-primary);
      background: var(--el-color-primary-light-9);
    }
    &.company{
      color: var(--el-color-success);
      background: var(--el-color-success-light-9);
    }
  }
  .item-arrow{
    color: var(--yq-font-color-desc);
    font-size: var(--yq-font-size-mini-mobile);
  }
}
</style>
